import moment from 'moment'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Tag, Button, Popconfirm, Space } from 'antd'
import usePrevious from 'hooks/usePrevious'
import useClient from 'hooks/useClient'
import { useDispatch, useSelector } from 'react-redux'
import { getMosqueRequests, updateMosqueRequestStatus } from 'redux/actions/mosqueRequests'
import { sortByAlphabet, sortByDate } from 'utils/sorters'
import { DATE_FORMAT, PERMISSIONS } from 'utils/constants'
import SortableColumnTitle from 'components/SortableColumnTitle'
import PageTable from 'components/PageTable'
import { isObjectsEqual } from 'utils/functions'

const STATUS_COLORS = {
  PENDING: 'orange',
  APPROVED: 'green',
  REJECTED: 'red',
}

const MosqueRequests = () => {
  const { t } = useTranslation()
  const { isClientCan, client } = useClient()
  const dispatch = useDispatch()
  const [search, setSearch] = useState('')
  const [tableFilter, setTableFilter] = useState({ status: null })
  const [loadingId, setLoadingId] = useState(null)
  const [isNeedLoadData, setIsNeedLoadData] = useState(false)
  const prevSearch = usePrevious(search)

  const { items, pagination, isGetLoading } = useSelector((state) => state.mosqueRequests)
  
  const isClientCanUpdateMosqueRequests = isClientCan(PERMISSIONS.UPDATE_MOSQUE_REQUESTS)

  const refreshMosqueRequests = (page, size, searchValue, status) => {
    dispatch(getMosqueRequests({ page, size, search: searchValue, status }))
    setIsNeedLoadData(false)
  }

  const handleStatusChange = (id, status) => {
    setLoadingId(id)
    dispatch(updateMosqueRequestStatus({ 
      id, 
      status,
      onSuccess: () => setLoadingId(null)
    }))
  }

  // Load data when client is loaded
  useEffect(() => {
    if (client.isLoaded && !items.length) {
      refreshMosqueRequests(pagination.current, pagination.pageSize, search, tableFilter.status?.[0])
    }
  }, [client.isLoaded])

  // Reload when search changes
  useEffect(() => {
    if (prevSearch !== undefined && search !== prevSearch) {
      setIsNeedLoadData(true)
    }
  }, [search, prevSearch])

  // Reload when filter changes
  useEffect(() => {
    if (isNeedLoadData) {
      refreshMosqueRequests(1, pagination.pageSize, search, tableFilter.status?.[0])
    }
  }, [isNeedLoadData])

  const tableChangeHandler = (tablePagination, filter) => {
    setTableFilter((prevFilter) => (isObjectsEqual(prevFilter, filter) ? prevFilter : filter))
    const status = filter?.status?.[0] || null
    refreshMosqueRequests(tablePagination.current, tablePagination.pageSize, search, status)
  }

  const columns = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'id',
      width: '80px',
      align: 'center',
    },
    {
      title: 'Mosque Name (Arabic)',
      dataIndex: 'mosqueNameAr',
      key: 'mosqueNameAr',
      sorter: (a, b) => sortByAlphabet(a.mosqueNameAr, b.mosqueNameAr),
    },
    {
      title: 'Area',
      dataIndex: ['area', 'name'],
      key: 'area',
      render: (text, record) => record.area?.name || '-',
    },
    {
      title: 'Block',
      dataIndex: 'block',
      key: 'block',
    },
    {
      title: 'Street',
      dataIndex: 'street',
      key: 'street',
    },
    {
      title: 'Avenue',
      dataIndex: 'avenue',
      key: 'avenue',
      render: (text) => text || '-',
    },
    {
      title: 'PACI Number',
      dataIndex: 'paciNumber',
      key: 'paciNumber',
      render: (text) => text || '-',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: '120px',
      filters: [
        { text: 'Pending', value: 'PENDING' },
        { text: 'Approved', value: 'APPROVED' },
        { text: 'Rejected', value: 'REJECTED' },
      ],
      filterMultiple: false,
      render: (status) => <Tag color={STATUS_COLORS[status] || 'default'}>{status}</Tag>,
    },
    {
      title: (props) => <SortableColumnTitle {...props} title={t('fields.date')} id="date" />,
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: (a, b) => sortByDate(a.createdAt, b.createdAt),
      render: (createdAt) => createdAt && moment(createdAt).format(DATE_FORMAT),
      defaultSortOrder: 'descend',
    },
    ...(isClientCanUpdateMosqueRequests
      ? [
          {
            title: t('fields.actions'),
            key: 'actions',
            width: '180px',
            align: 'center',
            render: (_, record) => {
              if (record.status !== 'PENDING') {
                return '-'
              }
              return (
                <Space size="small">
                  <Popconfirm
                    title="Are you sure you want to approve this request?"
                    onConfirm={() => handleStatusChange(record.id, 'APPROVED')}
                    okText={t('app.yes')}
                    cancelText={t('app.no')}
                  >
                    <Button
                      type="primary"
                      size="small"
                      loading={loadingId === record.id}
                    >
                      Approve
                    </Button>
                  </Popconfirm>
                  <Popconfirm
                    title="Are you sure you want to reject this request?"
                    onConfirm={() => handleStatusChange(record.id, 'REJECTED')}
                    okText={t('app.yes')}
                    cancelText={t('app.no')}
                  >
                    <Button
                      danger
                      size="small"
                      loading={loadingId === record.id}
                    >
                      Reject
                    </Button>
                  </Popconfirm>
                </Space>
              )
            },
          },
        ]
      : []),
  ]

  return (
    <PageTable
      pageTitle="Mosque Requests"
      baseData={items}
      columns={columns}
      loading={isGetLoading}
      onChange={tableChangeHandler}
      searchHandler={(value) => setSearch(value.trim())}
      pagination={{
        ...pagination,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        showSizeChanger: pagination.total >= 10,
      }}
    />
  )
}

export default MosqueRequests

